import { useState, useEffect, useCallback, type FC } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useWalkthrough } from "src/contexts/WalkthroughContext";
import { WALKTHROUGH_STEPS } from "./walkthrough-steps";

interface CutoutRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;
const BORDER_RADIUS = 8;

const WalkthroughOverlay: FC = () => {
  const { isActive, currentStep } = useWalkthrough();
  const [cutout, setCutout] = useState<CutoutRect | null>(null);

  const step = WALKTHROUGH_STEPS[currentStep];

  const calculateCutout = useCallback(() => {
    if (!step?.targetSelector) {
      setCutout(null);
      return;
    }

    const el = document.querySelector(step.targetSelector);
    if (!el) {
      setCutout(null);
      return;
    }

    const rect = el.getBoundingClientRect();
    setCutout({
      top: rect.top - PADDING,
      left: rect.left - PADDING,
      width: rect.width + PADDING * 2,
      height: rect.height + PADDING * 2,
    });
  }, [step?.targetSelector]);

  useEffect(() => {
    if (!isActive) return;
    calculateCutout();

    const handleResize = () => calculateCutout();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [isActive, calculateCutout]);

  // Recalculate when step changes
  useEffect(() => {
    if (!isActive) return;
    // Small delay to allow DOM updates
    const timer = requestAnimationFrame(() => {
      calculateCutout();
    });
    return () => cancelAnimationFrame(timer);
  }, [currentStep, isActive, calculateCutout]);

  if (!isActive) return null;

  // For modal steps (no target), show full overlay without cutout
  const clipPath = cutout
    ? `polygon(
        0% 0%, 0% 100%, 100% 100%, 100% 0%, 0% 0%,
        ${cutout.left}px ${cutout.top}px,
        ${cutout.left}px ${cutout.top + cutout.height}px,
        ${cutout.left + cutout.width}px ${cutout.top + cutout.height}px,
        ${cutout.left + cutout.width}px ${cutout.top}px,
        ${cutout.left}px ${cutout.top}px
      )`
    : undefined;

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          className="fixed inset-0 z-[9999] pointer-events-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Overlay backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50"
            style={clipPath ? { clipPath } : undefined}
            animate={clipPath ? { clipPath } : undefined}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          />

          {/* Cutout highlight border */}
          {cutout && (
            <motion.div
              className="absolute border-2 border-white/30 pointer-events-none"
              style={{
                borderRadius: BORDER_RADIUS,
              }}
              initial={false}
              animate={{
                top: cutout.top,
                left: cutout.left,
                width: cutout.width,
                height: cutout.height,
              }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WalkthroughOverlay;
